import { BellIcon } from '@heroicons/react/16/solid';
import { Field, FieldProps, Form, Formik, FormikProps } from 'formik';
import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import {
  AGENDA_REMINDER_FIELD,
  TEMPLATE_MODAL_SECTION_1,
  TEMPLATE_MODAL_SECTION_2,
} from '../../../Agenda.constants';
import { tManagementModalForm } from '../../../Agenda.types';
import { useAgendaContext } from '../../../hooks/agenda.hook';
import useAgendaTemplateManagementModal from '../../../hooks/agendaTemplateModal.hook';
import { useAgendaTemplatesContext } from '../../../hooks/agendaTemplateView.hook';
import { reminderOptions } from '../../Calendar/Calendar.constants';
import EmojiPickerComponent from '../../EmojiPicker/EmojiPicker.component';
import Switcher from '../../Switcher.component';
import FieldSwitcherRow from './FieldSwitcherRow.component';
import styles from './TemplateModal.module.scss';

// TODO: Redo form in Formik
export default function TemplateModal(): React.ReactElement {
  const { t } = useTranslation();
  const { currentEventTypes } = useAgendaContext();
  const { templateManagementModalRef, submitTemplateForm, noSelectionError } =
    useAgendaTemplateManagementModal();
  const {
    showTemplateModal,
    toggleTemplateModal,
    isTemplateEditionMode,
    TemplateManagementFormikOptions,
    selectedEmoji,
    setSelectedEmoji,
    creatingTemplate,
    currentTemplateFields,
    setCurrentTemplateFields,
  } = useAgendaTemplatesContext();

  const [isReminderToggled, setIsReminderToggled] = useState(
    Boolean(currentTemplateFields['reminder']),
  );

  useEffect(() => {
    if (isReminderToggled) {
      const fields = {
        ...currentTemplateFields,
        reminder: AGENDA_REMINDER_FIELD,
      };
      setCurrentTemplateFields(fields);

      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { reminder, ...fields } = currentTemplateFields;
    setCurrentTemplateFields(fields);
  }, [isReminderToggled]);

  return (
    <Modal.Legacy
      open={showTemplateModal}
      onClickBackdrop={() => toggleTemplateModal(false)}
      className="bg-base-100 w-[30rem] no-scrollbar rounded-md"
    >
      <Formik {...TemplateManagementFormikOptions}>
        {(formikProps: FormikProps<tManagementModalForm>) => {
          const { errors, touched } = formikProps;

          return (
            <Form ref={templateManagementModalRef}>
              <h1 className="text-xl mb-4">
                {isTemplateEditionMode
                  ? 'Edita tu template'
                  : 'Crea un nuevo template'}
              </h1>

              <hr className={styles.TemplateModalDivider} />

              <div className={styles.TemplateModal}>
                {/*Emoji and Name*/}
                <div>
                  <label htmlFor="templateTitle">
                    {t('agenda/agenda:add-emoji')}
                  </label>
                  <div className={styles.TemplateModalTitleAndEmojiField}>
                    <div
                      className={
                        styles.TemplateModalTitleAndEmojiField__EmojiContainer
                      }
                    >
                      <EmojiPickerComponent
                        inputComponent
                        emoji={selectedEmoji}
                        handleOnClick={(emojiData) => {
                          if (emojiData) {
                            setSelectedEmoji(emojiData.unified);
                          }
                        }}
                      />
                    </div>
                    <div
                      className={
                        styles.TemplateModalTitleAndEmojiField__TitleContainer
                      }
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5 my-auto text-gray-800"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 1 1 0-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 0 1-1.44-4.282m3.102.069a18.03 18.03 0 0 1-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 0 1 8.835 2.535M10.34 6.66a23.847 23.847 0 0 0 8.835-2.535m0 0A23.74 23.74 0 0 0 18.795 3m.38 1.125a23.91 23.91 0 0 1 1.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 0 0 1.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 0 1 0 3.46"
                        />
                      </svg>
                      <Field
                        name="templateTitle"
                        className={styles.TemplateModalTitle}
                        placeholder={t('agenda/agenda:title-placeholder')}
                      />
                    </div>
                  </div>
                  {touched.templateTitle && errors.templateTitle ? (
                    <div className={styles.TemplateModalError}>
                      {errors.templateTitle}
                    </div>
                  ) : null}
                </div>

                {/*Event Type*/}
                <div>
                  <label htmlFor="eventType">
                    {t('agenda/agenda:what-is-your-event')}
                  </label>
                  <div className={styles.TemplateModalEventType__Container}>
                    <BellIcon
                      className={styles.TemplateModalEventType__Container__Icon}
                    />
                    <Field
                      name="eventType"
                      as="select"
                      className={styles.TemplateModalEventType__Select}
                    >
                      <option className="text-neutral">
                        {t('agenda/agenda:choose-your-event')}
                      </option>
                      {currentEventTypes &&
                        Object.values(currentEventTypes)?.map((eventType) => (
                          <option key={eventType.id} value={eventType.id}>
                            {eventType.name}
                          </option>
                        ))}
                    </Field>
                  </div>
                </div>

                <hr className={styles.TemplateModalDivider} />

                {/*Description and Address*/}
                {TEMPLATE_MODAL_SECTION_1?.map((field) => (
                  <FieldSwitcherRow field={field} key={field.name} />
                ))}

                {/*Reminder*/}
                <div className={styles.TemplateModalReminder}>
                  <p className={styles.TemplateModalReminder__Text}>
                    {t('agenda/agenda:send-reminder')}
                  </p>
                  <Switcher
                    className={styles.TemplateModalReminder__Switcher}
                    reverse
                    invertColors
                    on={isReminderToggled}
                    changeHandler={() =>
                      setIsReminderToggled(!isReminderToggled)
                    }
                  />
                </div>
                {isReminderToggled && (
                  <>
                    <label className={styles.TemplateModalReminder__Label}>
                      {t('agenda/agenda:how-much-time')}
                    </label>
                    <Field
                      className={styles.Input}
                      name="reminderMinutes"
                      id="reminderMinutes"
                    >
                      {({ field }: FieldProps<string>) => (
                        <select
                          {...field}
                          className={styles.TemplateModalReminder__Select}
                        >
                          {reminderOptions.map((option) => (
                            <option value={option.value} key={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </Field>
                  </>
                )}

                <hr className={styles.TemplateModalDivider} />

                {/*Links, Guests, and Notes*/}
                {TEMPLATE_MODAL_SECTION_2?.map((field) => (
                  <FieldSwitcherRow field={field} key={field.name} />
                ))}

                {noSelectionError && (
                  <div className={styles.TemplateModalError}>
                    {t('agenda/agenda:at-least-one-field')}
                  </div>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
      <Modal.Actions className="DeleteFunnelModal__Actions">
        <Button
          variant="outline"
          onClick={() => {
            toggleTemplateModal(false);
          }}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          color="primary"
          className="text-base-100"
          loading={creatingTemplate}
          onClick={submitTemplateForm}
        >
          Guardar
        </Button>
      </Modal.Actions>
    </Modal.Legacy>
  );
}
